<template>
    <div :class="['col-span-12 mb-5', addClass]">
        <label v-if="label" class="mb-3 block text-sm font-semibold uppercase text-principal">
            {{ label }}
        </label>

        <input ref="cameraInput" class="hidden" type="file" :accept="accept" capture="environment"
            :disabled="controlsDisabled" @change="selectFile" />
        <input ref="galleryInput" class="hidden" type="file" :accept="accept"
            :disabled="controlsDisabled" @change="selectFile" />

        <div v-if="imagePreview"
            class="relative aspect-[4/3] overflow-hidden rounded-md border border-[var(--color-lineas)] bg-[var(--color-fondo-ficha)]">
            <img :src="imagePreview" alt="Fotografia acquisita sul campo" class="h-full w-full object-contain" />
            <img v-if="drawingPreview" :src="drawingPreview" alt=""
                class="pointer-events-none absolute inset-0 h-full w-full object-contain" />
            <button type="button" title="Disegna sulla fotografia" aria-label="Disegna sulla fotografia"
                :class="[
                    'absolute bottom-2 right-2 flex h-11 w-11 items-center justify-center rounded-md text-white shadow',
                    controlsDisabled || editorLoading
                        ? 'cursor-not-allowed bg-[var(--color-contador)]'
                        : 'bg-principal',
                ]"
                :disabled="controlsDisabled || editorLoading" @click="openEditor">
                <Icon name="pencil" class="h-5 w-5" />
            </button>
        </div>

        <div class="mt-2 flex flex-wrap gap-2">
            <button type="button"
                :class="[
                    'flex min-h-11 items-center gap-2 rounded-md px-4 text-xs font-bold uppercase tracking-wide text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-principal',
                    controlsDisabled
                        ? 'cursor-not-allowed bg-[var(--color-contador)]'
                        : 'bg-principal hover:bg-principal',
                ]"
                :disabled="controlsDisabled" @click="cameraInput?.click()">
                <Icon name="photo" class="h-5 w-5" />
                {{ imagePreview ? 'Sostituisci foto' : 'Scatta foto' }}
            </button>
            <button type="button" title="Scegli dalla galleria" aria-label="Scegli dalla galleria"
                :class="[
                    'flex h-11 w-11 items-center justify-center rounded-md border bg-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-principal',
                    controlsDisabled
                        ? 'cursor-not-allowed border-[var(--color-contador)] text-[var(--color-contador)]'
                        : 'border-principal text-principal hover:bg-[var(--color-fondo-ficha)]',
                ]"
                :disabled="controlsDisabled" @click="galleryInput?.click()">
                <Icon name="folder-open" class="h-5 w-5" />
            </button>
            <button v-if="drawingPreview" type="button"
                :class="[
                    'flex min-h-11 items-center gap-2 rounded-md border bg-white px-3 text-xs font-bold uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-red)]',
                    controlsDisabled
                        ? 'cursor-not-allowed border-[var(--color-contador)] text-[var(--color-contador)]'
                        : 'border-[var(--color-red)] text-[var(--color-red)] hover:bg-[var(--color-fondo-ficha)]',
                ]"
                :disabled="controlsDisabled" @click="clearDrawing">
                <Icon name="trash" class="h-4 w-4" />
                Rimuovi disegno
            </button>
        </div>

        <p v-if="processing" class="mt-2 text-xs font-semibold text-[var(--color-contador)]">Preparazione fotografia…</p>
        <p v-if="storageWarning" class="mt-2 text-xs font-semibold text-[var(--color-red)]">{{ storageWarning }}</p>
        <p v-if="errorMessage" class="mt-2 text-xs font-semibold text-[var(--color-red)]">{{ errorMessage }}</p>
    </div>

    <HeraPhotoDrawingEditor v-if="editorImage" :image="editorImage" :drawing="editorDrawing"
        @close="closeEditor" @save="saveDrawing" @error="handleError" />
</template>

<script>
import { defineElement } from '@vueform/vueform';
import axios from 'axios';
import { computed, onBeforeUnmount, ref, unref, watch } from 'vue';
import Icon from '@/Components/Icon.vue';
import { useToast } from '@/composables/useToast';
import { getTenantContext } from '@/composables/useTenant';
import {
    estimateMaintenanceStorage,
    getMaintenancePhoto,
    putMaintenancePhoto,
} from '@/modules/hera-maintenance/offline/maintenanceDb';
import { compressMaintenancePhoto } from '@/modules/hera-maintenance/offline/maintenancePhotos';
import { uploadTemporaryFile } from '@/modules/hera-maintenance/offline/uploadTemporaryFile';
import HeraPhotoDrawingEditor from './HeraPhotoDrawingEditor.vue';

const truthy = value => value === true || value === 'true' || value === 1 || value === '1';
const localPhotoId = value => value && typeof value === 'object'
    ? (value.localPhotoId ?? null)
    : null;

const valueAtPath = (source, path) => String(path ?? '')
    .split('.')
    .filter(Boolean)
    .reduce((value, segment) => value?.[segment], source);

export default defineElement({
    name: 'HeraMaintenancePhotoElement',
    inheritAttrs: false,
    components: { HeraPhotoDrawingEditor, Icon },
    props: {
        modelValue: { type: [Object, String], default: null },
        name: { type: String, required: true },
        label: { type: String, default: '' },
        sheetName: { type: String, required: true },
        createMode: { type: Boolean, default: false },
        accept: { type: String, default: 'image/*' },
        addClass: { type: [String, Array, Object], default: '' },
    },
    emits: ['update:modelValue', 'change'],
    setup(props, { emit, element }) {
        const toast = useToast();
        const tenant = getTenantContext();
        const scope = { tenantId: tenant.id, userId: tenant.user?.id };
        const draftKey = `${props.sheetName}:new`;
        const cameraInput = ref(null);
        const galleryInput = ref(null);
        const processing = ref(false);
        const editorLoading = ref(false);
        const editorImage = ref(null);
        const editorDrawing = ref(null);
        const imageBlob = ref(null);
        const drawingBlob = ref(null);
        const imageObjectUrl = ref('');
        const drawingObjectUrl = ref('');
        const errorMessage = ref('');
        const storageWarning = ref('');
        let localLoadSequence = 0;

        const offlineMode = computed(() => props.createMode
            && truthy(tenant.settings['feature.offline_field_ops']));
        const fieldValue = computed(() => element?.model ? element.model.value : props.modelValue);
        const dataPath = computed(() => unref(element?.dataPath) || props.name);
        const rowPath = computed(() => dataPath.value.split('.').slice(0, -1).join('.'));
        const siblingPath = name => [rowPath.value, name].filter(Boolean).join('.');
        const formInstance = () => unref(element?.form$);
        const formModel = computed(() => {
            const form = formInstance();
            return unref(form?.model) ?? unref(form?.internalData) ?? {};
        });
        const drawingValue = computed(() => valueAtPath(formModel.value, siblingPath('drawing')) ?? null);
        const controlsDisabled = computed(() => Boolean(
            unref(element?.isDisabled)
            || unref(element?.isReadonly)
            || processing.value,
        ));
        const imagePreview = computed(() => imageObjectUrl.value
            || (typeof fieldValue.value === 'string' ? fieldValue.value : ''));
        const drawingPreview = computed(() => drawingObjectUrl.value
            || (typeof drawingValue.value === 'string' ? drawingValue.value : ''));

        const replaceObjectUrl = (target, blob) => {
            if (target.value) URL.revokeObjectURL(target.value);
            target.value = blob ? URL.createObjectURL(blob) : '';
        };

        watch(imageBlob, blob => replaceObjectUrl(imageObjectUrl, blob));
        watch(drawingBlob, blob => replaceObjectUrl(drawingObjectUrl, blob));

        const updateField = value => {
            if (element?.model) element.model.value = value;
            emit('update:modelValue', value);
            emit('change', value);
        };

        const updateSibling = (name, value) => {
            formInstance()?.updateModel?.(siblingPath(name), value);
        };

        const rowValue = name => valueAtPath(formModel.value, siblingPath(name));

        const handleError = (error, fallback = 'Impossibile preparare la fotografia.') => {
            errorMessage.value = error?.message || fallback;
            toast.error(errorMessage.value);
        };

        const currentLocation = async () => {
            if (typeof navigator === 'undefined' || !navigator.geolocation) return {};
            try {
                const position = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(
                    resolve,
                    reject,
                    { enableHighAccuracy: true, timeout: 5000, maximumAge: 30000 },
                ));
                return {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    locationAccuracy: position.coords.accuracy,
                };
            } catch {
                return {};
            }
        };

        const updateCaptureMetadata = metadata => {
            ['capturedAt', 'latitude', 'longitude', 'locationAccuracy'].forEach((name) => {
                if (metadata[name] !== null && typeof metadata[name] !== 'undefined') {
                    updateSibling(name, metadata[name]);
                }
            });
        };

        const selectFile = async event => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (!file || controlsDisabled.value) return;

            processing.value = true;
            errorMessage.value = '';
            try {
                const [photo, location] = await Promise.all([
                    compressMaintenancePhoto(file),
                    currentLocation(),
                ]);
                const previousLocalId = localPhotoId(fieldValue.value);
                const id = previousLocalId ?? crypto.randomUUID();

                if (offlineMode.value) {
                    await putMaintenancePhoto(scope, {
                        id,
                        draftKey,
                        ...photo,
                        ...location,
                        drawingBlob: null,
                        drawingTempFile: null,
                    });
                    updateField({
                        localPhotoId: id,
                        originalName: photo.originalName,
                        mimeType: photo.mimeType,
                    });
                } else {
                    updateField(await uploadTemporaryFile(photo.blob, photo.originalName));
                }

                imageBlob.value = photo.blob;
                drawingBlob.value = null;
                updateSibling('drawing', null);
                updateCaptureMetadata({ ...photo, ...location });

                if (offlineMode.value) {
                    const estimate = await estimateMaintenanceStorage();
                    storageWarning.value = estimate.ratio !== null && estimate.ratio >= 0.8
                        ? 'Spazio del dispositivo quasi esaurito: sincronizza o rimuovi alcune foto.'
                        : '';
                }
            } catch (error) {
                handleError(error);
            } finally {
                processing.value = false;
            }
        };

        const fetchRemoteBlob = async url => {
            const response = await axios.get('/api/file-proxy', {
                params: { url },
                responseType: 'blob',
            });
            return response.data;
        };

        const openEditor = async () => {
            if (controlsDisabled.value || editorLoading.value) return;
            editorLoading.value = true;
            errorMessage.value = '';
            try {
                const image = imageBlob.value
                    ?? fieldValue.value?.__file__
                    ?? (typeof fieldValue.value === 'string' ? await fetchRemoteBlob(fieldValue.value) : null);
                if (!image) throw new Error('La fotografia non può essere aperta per il disegno.');

                let drawing = drawingBlob.value ?? drawingValue.value?.__file__ ?? null;
                if (!drawing && typeof drawingValue.value === 'string') {
                    drawing = await fetchRemoteBlob(drawingValue.value);
                }

                editorImage.value = image;
                editorDrawing.value = drawing;
            } catch (error) {
                handleError(error, 'La fotografia non può essere aperta per il disegno.');
            } finally {
                editorLoading.value = false;
            }
        };

        const closeEditor = () => {
            editorImage.value = null;
            editorDrawing.value = null;
        };

        const saveDrawing = async blob => {
            processing.value = true;
            errorMessage.value = '';
            try {
                if (!blob) {
                    await clearDrawing();
                    closeEditor();
                    return;
                }

                const localId = localPhotoId(fieldValue.value);
                const originalName = `${rowValue('id') ?? localId ?? 'photo'}-drawing.png`;
                if (offlineMode.value) {
                    const photo = await getMaintenancePhoto(scope, localId);
                    if (!photo) throw new Error('La fotografia locale non è più disponibile.');
                    await putMaintenancePhoto(scope, {
                        ...photo,
                        drawingBlob: blob,
                        drawingOriginalName: originalName,
                        drawingTempFile: null,
                    });
                    updateSibling('drawing', { localPhotoId: localId, originalName });
                } else {
                    updateSibling('drawing', await uploadTemporaryFile(blob, originalName));
                }

                drawingBlob.value = blob;
                closeEditor();
            } catch (error) {
                handleError(error, 'Impossibile salvare il disegno.');
            } finally {
                processing.value = false;
            }
        };

        const clearDrawing = async () => {
            const localId = localPhotoId(fieldValue.value);
            if (offlineMode.value && localId) {
                const photo = await getMaintenancePhoto(scope, localId);
                if (photo) {
                    await putMaintenancePhoto(scope, {
                        ...photo,
                        drawingBlob: null,
                        drawingOriginalName: null,
                        drawingTempFile: null,
                    });
                }
            }
            drawingBlob.value = null;
            updateSibling('drawing', null);
        };

        watch(
            () => localPhotoId(fieldValue.value),
            async id => {
                const sequence = ++localLoadSequence;
                if (!id || !offlineMode.value) return;
                try {
                    const photo = await getMaintenancePhoto(scope, id);
                    if (sequence !== localLoadSequence || !photo) return;
                    imageBlob.value = photo.blob ?? null;
                    drawingBlob.value = photo.drawingBlob ?? null;
                } catch (error) {
                    if (sequence === localLoadSequence) handleError(error, 'La fotografia locale non è disponibile.');
                }
            },
            { immediate: true },
        );

        onBeforeUnmount(() => {
            replaceObjectUrl(imageObjectUrl, null);
            replaceObjectUrl(drawingObjectUrl, null);
        });

        return {
            cameraInput,
            galleryInput,
            processing,
            editorLoading,
            editorImage,
            editorDrawing,
            imagePreview,
            drawingPreview,
            controlsDisabled,
            errorMessage,
            storageWarning,
            selectFile,
            openEditor,
            closeEditor,
            saveDrawing,
            clearDrawing,
            handleError,
        };
    },
});
</script>